import React, { useEffect } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Colors } from 'library';
import { ExpandableCard, PillButton, Row, Stack, Text } from 'library/ui';

interface FormSectionProps {
    title: string;
    subtitle?: string;
    icon: React.ReactElement;
    expanded: boolean;
    complete: boolean;
    onToggle: () => void;
    onNext?: () => void;
    autoFocusId?: string;
    children: React.ReactNode;
}

export default function FormSection({
    title,
    subtitle,
    icon,
    expanded,
    complete,
    onToggle,
    onNext,
    autoFocusId,
    children,
}: FormSectionProps) {
    useEffect(() => {
        if (expanded && autoFocusId) {
            const timer = setTimeout(() => document.getElementById(autoFocusId)?.focus(), 250);
            return () => clearTimeout(timer);
        }
    }, [expanded, autoFocusId]);

    const header = (
        <Row justify="between" align="center" fullWidth>
            <Row gap="sm" align="center">
                {React.cloneElement(icon, {
                    sx: { color: complete ? Colors.secondary : Colors.textSecondary, fontSize: 20 },
                })}
                <Stack gap="none">
                    <Text weight="bold">{title}</Text>
                    {!expanded && complete && (
                        <Text size="xs" tone="accent">
                            Completed
                        </Text>
                    )}
                    {!expanded && !complete && subtitle && (
                        <Text size="xs" tone="secondary">
                            {subtitle}
                        </Text>
                    )}
                </Stack>
            </Row>
            {complete && <CheckCircleIcon sx={{ color: Colors.secondary, fontSize: 22 }} />}
        </Row>
    );

    return (
        <ExpandableCard header={header} expanded={expanded} onExpandedChange={onToggle}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onNext && onNext();
                }}
            >
                <Stack gap="sm">
                    {subtitle && (
                        <Text size="xs" tone="secondary">
                            {subtitle}
                        </Text>
                    )}
                    <Row wrap>{children}</Row>
                    {onNext && (
                        <Row justify="end">
                            <PillButton id={`${title}-next`} type="submit" disabled={!complete}>
                                Next
                            </PillButton>
                        </Row>
                    )}
                </Stack>
            </form>
        </ExpandableCard>
    );
}
